import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/auth';
import { Card, Pill } from '../lib/ui';
import '../farmDashboard.css';

const POLL_MS = 30_000;

function healthPill(h) {
  if (!h) return null;
  const pillStyle = (color) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, border: `1px solid ${color}44` });
  if (h === 'good')          return <Pill bg="color-mix(in oklab, #10b981 12%, var(--panel))" fg="#10b981" style={pillStyle('#10b981')}>✓ {h}</Pill>;
  if (h.startsWith('fair'))  return <Pill bg="color-mix(in oklab, #f59e0b 12%, var(--panel))" fg="#f59e0b" style={pillStyle('#f59e0b')}>~ {h}</Pill>;
  if (h.startsWith('poor'))  return <Pill bg="color-mix(in oklab, #ef4444 12%, var(--panel))" fg="#ef4444" style={pillStyle('#ef4444')}>✗ {h}</Pill>;
  return <Pill bg="var(--panel-2)" fg="var(--muted)" style={pillStyle('var(--muted)')}>{h}</Pill>;
}

function StatRow({ label, value, sub }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '6px 0', borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
        {value ?? '-'}
        {sub && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)', marginLeft: 5 }}>{sub}</span>}
      </span>
    </div>
  );
}

function RecentPoint({ p, isEC, onDelete }) {
  const HIGH_SD = 3;
  const unstable = typeof p.mV_sd === 'number' && p.mV_sd > HIGH_SD;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1.4fr 0.8fr 0.8fr 0.7fr 0.9fr auto',
      gap: 8, alignItems: 'center',
      padding: '6px 10px',
      borderRadius: 'var(--r-sm)',
      background: unstable
        ? 'color-mix(in oklab, #f59e0b 10%, var(--panel))'
        : 'var(--panel-2)',
      border: `1px solid ${unstable ? 'color-mix(in oklab, #f59e0b 40%, var(--border))' : 'var(--border)'}`,
      fontSize: 12,
    }}>
      <span style={{ color: 'var(--muted)', fontSize: 11 }}>
        {p.ts ? new Date(p.ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
      </span>
      <span>{typeof p.mV_avg === 'number' ? `${p.mV_avg.toFixed(1)} mV` : '-'}</span>
      <span style={{ color: unstable ? '#f59e0b' : 'inherit' }}>
        SD {typeof p.mV_sd === 'number' ? p.mV_sd.toFixed(1) : '-'}{unstable ? ' ⚠' : ''}
      </span>
      <span style={{ fontWeight: 700 }}>{isEC ? `${p.measured} mS` : `pH ${p.measured}`}</span>
      <span style={{ color: 'var(--muted)', fontSize: 11 }}>
        {typeof p.waterTempC === 'number' ? `${p.waterTempC.toFixed(1)} °C` : 'no temp'}
      </span>
      <button
        onClick={() => onDelete(p.ts)}
        title="Remove this calibration point"
        style={{
          margin: 0, padding: '2px 7px', fontSize: 12,
          background: 'color-mix(in oklab, #ef4444 10%, var(--panel))',
          color: '#ef4444',
          border: '1px solid color-mix(in oklab, #ef4444 30%, var(--border))',
          borderRadius: 'var(--r-sm)',
          cursor: 'pointer',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

function SensorCard({ title, data, isEC, accentColor, onCalibrate, calibrating, onDeletePoint }) {
  if (!data) return null;
  const { pointCount, validForModel, filteredOut, model, r2, tempRangeC, unstablePoints, recentPoints, health } = data;

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 5, height: 36, borderRadius: 3, background: accentColor, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{title}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
              {isEC ? 'All estimates reported at 25 °C reference' : 'mV Nernst-normalised to 25 °C'}
            </div>
          </div>
        </div>
        {healthPill(health)}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
        <StatRow label="Calibration points" value={`${pointCount} total · ${validForModel} valid`}
          sub={filteredOut > 0 ? `${filteredOut} filtered out` : undefined} />
        <StatRow label="Unstable readings (mV SD > 3)"
          value={unstablePoints > 0 ? `${unstablePoints} ⚠` : '0'} />
        <StatRow label="R²"
          value={r2 != null ? r2.toFixed(4) : '-'}
          sub={r2 != null ? (r2 >= 0.97 ? 'good fit' : r2 >= 0.90 ? 'fair fit' : 'poor fit - add points') : 'need ≥2 points'} />
        <StatRow label="Model (y = a·mV + b)"
          value={model ? `a = ${model.a.toFixed(5)},  b = ${model.b.toFixed(3)}` : 'no model yet'} />
        <StatRow label="Calibration temp range"
          value={tempRangeC ? `${tempRangeC.minC} – ${tempRangeC.maxC} °C` : '-'} />
      </div>

      {recentPoints?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Recent calibration points
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 0.8fr 0.8fr 0.7fr 0.9fr auto',
              gap: 8, padding: '0 10px',
              fontSize: 11, fontWeight: 700, color: 'var(--muted)',
            }}>
              <span>Recorded</span><span>mV avg</span><span>mV SD</span><span>Reference</span><span>Water temp</span><span></span>
            </div>
            {recentPoints.map((p, i) => <RecentPoint key={i} p={p} isEC={isEC} onDelete={onDeletePoint} />)}
          </div>
        </div>
      )}

      <CalibrationForm isEC={isEC} onSubmit={onCalibrate} submitting={calibrating} accentColor={accentColor} />
    </Card>
  );
}

function CalibrationForm({ isEC, onSubmit, submitting, accentColor }) {
  const [value, setValue] = useState('');
  const label = isEC ? 'Reference EC (mS/cm at 25 °C)' : 'Reference pH';
  const placeholder = isEC ? 'e.g. 1.413' : 'e.g. 7.0';

  const handleSubmit = (e) => {
    e.preventDefault();
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return;
    onSubmit(isEC ? { ec: n } : { ph: n });
    setValue('');
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 4 }}>
      <div style={{ flex: 1 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 4, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {label}
        </label>
        <input
          type="number" step="any" placeholder={placeholder}
          value={value} onChange={e => setValue(e.target.value)}
          style={{
            width: '100%', padding: '8px 10px',
            borderRadius: 'var(--r-md)',
            border: '1px solid var(--border)',
            fontSize: 13,
            background: 'var(--panel)',
            color: 'var(--text)',
          }}
        />
      </div>
      <button
        type="submit" disabled={submitting || !value}
        style={{
          padding: '8px 16px',
          background: submitting ? 'var(--muted)' : accentColor,
          margin: 0,
          flexShrink: 0,
        }}
      >
        {submitting ? 'Saving…' : '+ Add point'}
      </button>
    </form>
  );
}

function LiveEstimates({ data }) {
  if (!data) return null;
  const { waterTempC, pH, EC } = data;

  const row = (label, mv, sd, est, storedEst, unit) => (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      padding: '8px 12px',
      borderRadius: 'var(--r-sm)',
      background: 'var(--panel-2)',
      border: '1px solid var(--border)',
      fontSize: 13,
    }}>
      <span style={{ fontWeight: 700, minWidth: 28, color: 'var(--brand)' }}>{label}</span>
      <span style={{ color: 'var(--muted)', minWidth: 72 }}>{mv != null ? `${mv} mV` : '-'}</span>
      <span style={{ color: 'var(--muted)', fontSize: 11, minWidth: 56 }}>SD {sd != null ? sd : '-'}</span>
      <span style={{ fontWeight: 700, color: 'var(--brand-2)', minWidth: 72 }}>
        {est != null ? `${est} ${unit}` : '-'}
      </span>
      <span style={{ color: 'var(--muted)', fontSize: 11 }}>
        stored: {storedEst != null ? `${storedEst} ${unit}` : '-'}
        {est != null && storedEst != null && Math.abs(est - storedEst) > 0.02
          ? <span style={{ color: '#f59e0b', marginLeft: 4, fontWeight: 700 }}>Δ{Math.abs(est - storedEst).toFixed(3)}</span>
          : null}
      </span>
    </div>
  );

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: 20, color: 'var(--brand)', letterSpacing: '-0.01em' }}>
          Live Estimates
        </div>
        <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>
          Water {waterTempC != null ? `${waterTempC} °C` : '-'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 6, padding: '0 12px', marginBottom: 6, fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        <span style={{ minWidth: 28 }}>Sensor</span>
        <span style={{ minWidth: 72 }}>mV avg</span>
        <span style={{ minWidth: 56 }}>mV SD</span>
        <span style={{ minWidth: 72 }}>New est.</span>
        <span>Stored</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {row('pH',  pH?.mV_avg,  pH?.mV_sd,  pH?.est,        pH?.storedEst,  '')}
        {row('EC',  EC?.mV_avg,  EC?.mV_sd,  EC?.est_at_25C, EC?.storedEst,  'mS/cm')}
      </div>

      {(pH?.est == null || EC?.est_at_25C == null) && (
        <div style={{ marginTop: 12, fontSize: 12, color: '#f59e0b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>⚠</span>
          <span>One or more sensors has no active model - add at least 2 calibration points.</span>
        </div>
      )}
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CalibrationPage() {
  const [status, setStatus]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [calibrating, setCalibrating] = useState(false);
  const [calResult, setCalResult]     = useState(null);
  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/sensor-data/calibration/status');
      setStatus(res.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load calibration status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    timerRef.current = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchStatus]);

  const handleCalibrate = useCallback(async (body) => {
    setCalibrating(true);
    setCalResult(null);
    try {
      const res = await api.post('/sensor-data/calibration', body);
      setCalResult({ ok: true, warnings: res.data.warnings });
      await fetchStatus();
    } catch (e) {
      setCalResult({ ok: false, error: e?.response?.data?.error || e.message });
    } finally {
      setCalibrating(false);
      setTimeout(() => setCalResult(null), 8000);
    }
  }, [fetchStatus]);

  const handleDeletePoint = useCallback(async (type, ts) => {
    if (!window.confirm('Remove this calibration point? This cannot be undone.')) return;
    try {
      await api.delete(`/sensor-data/calibration/${type}/${encodeURIComponent(ts)}`);
      await fetchStatus();
      setCalResult({ ok: true, deleted: true, warnings: [] });
      setTimeout(() => setCalResult(null), 4000);
    } catch (e) {
      setCalResult({ ok: false, error: e?.response?.data?.error || e.message });
    }
  }, [fetchStatus]);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontWeight: 400,
            fontSize: 18,
            textAlign: 'left',
            letterSpacing: '-0.01em',
            color: 'var(--brand)',
            lineHeight: 1.1,
          }}>
            Sensor Calibration
          </h1>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            EC@25°C · pH Nernst-compensated · auto-refreshes every 30 s
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {lastUpdated && (
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>
              Updated {lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button onClick={fetchStatus} style={{ margin: 0 }}>Refresh</button>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--muted)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          Loading…
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 'var(--r-md)', marginBottom: 16,
          background: 'color-mix(in oklab, #ef4444 10%, var(--panel))',
          border: '1px solid color-mix(in oklab, #ef4444 30%, var(--border))',
          color: '#ef4444', fontWeight: 600,
        }}>
          ⚠ {error}
        </div>
      )}

      {calResult && (
        <div style={{
          padding: '12px 16px', borderRadius: 'var(--r-md)', marginBottom: 16,
          background: calResult.ok
            ? 'color-mix(in oklab, var(--brand-2) 10%, var(--panel))'
            : 'color-mix(in oklab, #ef4444 10%, var(--panel))',
          border: `1px solid ${calResult.ok
            ? 'color-mix(in oklab, var(--brand-2) 35%, var(--border))'
            : 'color-mix(in oklab, #ef4444 30%, var(--border))'}`,
          color: calResult.ok ? 'var(--brand-2)' : '#ef4444',
          fontWeight: 600,
        }}>
          {calResult.ok
            ? (calResult.deleted ? '✓ Calibration point removed' : '✓ Calibration point saved')
            : `✗ ${calResult.error}`}
          {calResult.warnings?.map((w, i) => (
            <div key={i} style={{ fontWeight: 400, marginTop: 4, color: '#92400e', fontSize: 13 }}>⚠ {w}</div>
          ))}
        </div>
      )}

      {status && (
        <>
          <LiveEstimates data={status.liveEstimates} />

          {status.note && (
            <div style={{
              fontSize: 12, color: 'var(--muted)', marginBottom: 16,
              padding: '8px 12px',
              background: 'var(--panel-2)',
              borderRadius: 'var(--r-sm)',
              borderLeft: '3px solid var(--border)',
            }}>
              {status.note}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SensorCard
              title="pH"
              data={status.pH}
              isEC={false}
              accentColor="var(--brand)"
              onCalibrate={handleCalibrate}
              calibrating={calibrating}
              onDeletePoint={(ts) => handleDeletePoint('ph', ts)}
            />
            <SensorCard
              title="EC"
              data={status.EC}
              isEC={true}
              accentColor="var(--brand-2)"
              onCalibrate={handleCalibrate}
              calibrating={calibrating}
              onDeletePoint={(ts) => handleDeletePoint('ec', ts)}
            />
          </div>
        </>
      )}
    </div>
  );
}
